import React, { useEffect, useState, useRef } from 'react';
import { VendorTaglineControl } from '../../../../components/VendorTaglineField';
import { notify } from '../../../../utilities/notify';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Switch, FormControlLabel, TextField } from '@material-ui/core';
import { Alert as MuiAlert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { fetchRetailerWholesalerProfile, updateRetailerWholesalerProfile } from '../../../../actions/retailerWholesalerProfileActions';
import { uploadExactVendorProfileMedia } from '../../../../utilities/imageUpload';
import { getUserFromLocalStorage } from '../../../../utilities/user.js';
import {
    AddAPhoto,
    DeleteOutline,
    PhotoCamera,
    Panorama,
    Store,
    NotificationsNone,
    Settings,
    Warning,
    CheckCircleOutline,
    InfoOutlined,
    PhoneAndroid,
    LockOutlined,
    AccessTime,
    LocalOffer,
    ReceiptOutlined,
} from '@material-ui/icons';
import { apiSetCashierPin, apiUpdateVendorOpenHours, apiFetchRetailerWholesalerSections } from '../../../../api';
import VendorProfileMediaDeleteDialog from '../../../../components/VendorProfileMediaDeleteDialog';
import { useVendorMediaCrop } from '../../../../components/VendorMediaCrop';
import ConfirmDialog, { useConfirmDialog } from '../../../../components/ConfirmDialog';
import StoreOfferCard from '../../../../components/StoreOfferCard';
import OpenHoursManager from '../../../../components/OpenHours/OpenHoursManager';
import AlertSoundPrefs from '../../../../components/AlertSoundPrefs';
import VendorAlertSettings from '../../../../components/VendorAlertSettings';
import VendorTaxSettings from '../../../../components/VendorTaxSettings';
import { useGate } from '../../../../components/team/Gated';

// ─── Logo: 588×558 px  |  Banner: 1440×175 px ────────────────────────────────
const LOGO_W = 588;
const LOGO_H = 558;
const BANNER_W = 1440;
const BANNER_H = 175;

const useStyles = makeStyles(() => ({
    muiLabel: { fontWeight: 'normal', fontSize: '12px', textTransform: 'capitalize' },
}));

/* ─── Inline styles ───────────────────────────────────────────────────────── */
const S = {
    page: { padding: '0 0 40px', background: '#f8fafc', minHeight: '100vh' },

    // ── banner hero ──
    bannerWrap: {
        position: 'relative',
        width: '100%',
        height: '160px',
        background: 'linear-gradient(135deg, #005370 0%, #0291a1 100%)',
        overflow: 'hidden',
        flexShrink: 0,
    },
    bannerImg: { width: '100%', height: '100%', objectFit: 'cover' },
    bannerOverlay: {
        position: 'absolute', inset: 0,
        background: 'rgba(0,0,0,0.22)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
        padding: '12px 16px',
    },
    bannerEditBtn: {
        display: 'flex', alignItems: 'center', gap: '6px',
        background: 'rgba(255,255,255,0.18)',
        border: '1.5px solid rgba(255,255,255,0.5)',
        borderRadius: '8px',
        color: '#fff', fontWeight: 600, fontSize: '0.78rem',
        padding: '6px 12px', cursor: 'pointer',
        backdropFilter: 'blur(4px)',
    },

    // ── logo avatar ──
    logoWrap: {
        position: 'relative',
        width: '80px', height: '80px',
        borderRadius: '16px',
        border: '3px solid #fff',
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        overflow: 'hidden',
        background: '#e8f8fb',
        cursor: 'pointer',
        flexShrink: 0,
    },
    logoImg: { width: '100%', height: '100%', objectFit: 'cover' },
    logoPlaceholder: {
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#0291a1',
    },
    logoEditOverlay: {
        position: 'absolute', inset: 0,
        background: 'rgba(0,0,0,0.42)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: 0, transition: 'opacity 0.18s',
    },

    // ── profile header row — sits BELOW banner, no negative margin ──
    profileHeader: {
        padding: '16px 28px 4px',
        display: 'flex', alignItems: 'center', gap: '16px',
        background: '#fff',
        borderBottom: '1px solid #f1f5f9',
        marginBottom: '0',
    },
    shopName: {
        fontWeight: 700, fontSize: '1.2rem', color: '#111827', lineHeight: 1.3,
    },
    shopStatus: (active) => ({
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        background: active ? '#dcfce7' : '#fee2e2',
        color: active ? '#16a34a' : '#dc2626',
        border: `1px solid ${active ? '#86efac' : '#fecaca'}`,
        borderRadius: '20px', padding: '2px 10px',
        fontSize: '0.72rem', fontWeight: 700, marginTop: '4px',
    }),

    // ── tabs ──
    tabBar: {
        display: 'flex', gap: 0,
        borderBottom: '1px solid #e2e8f0',
        padding: '0 28px',
        background: '#fff',
        overflowX: 'auto',
    },
    tab: (active) => ({
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '12px 18px',
        fontWeight: active ? 700 : 500,
        fontSize: '0.85rem',
        color: active ? '#0291a1' : '#6b7280',
        // Longhand-only borders (avoid mixing `border` shorthand with `borderBottom`,
        // which makes React warn on active-tab change).
        borderTop: 'none', borderLeft: 'none', borderRight: 'none',
        borderBottom: active ? '2px solid #0291a1' : '2px solid transparent',
        cursor: 'pointer',
        background: 'none', outline: 'none',
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
    }),

    // ── content card ──
    card: {
        margin: '24px 28px',
        background: '#fff',
        borderRadius: '14px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        padding: '28px',
    },
    sectionTitle: {
        fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        marginBottom: '16px', marginTop: '4px',
    },
    fieldGrid: {
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px',
    },
    fieldFull: { gridColumn: '1 / -1' },

    // ── image upload panels ──
    imgPanel: {
        border: '1.5px dashed #e2e8f0',
        borderRadius: '12px',
        background: '#fafafa',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', position: 'relative',
    },
    imgActions: {
        display: 'flex', gap: '12px', marginTop: '10px',
        justifyContent: 'center',
    },
    imgActionBtn: (color) => ({
        background: 'none', border: 'none', cursor: 'pointer',
        color, fontWeight: 600, fontSize: '0.8rem',
        display: 'flex', alignItems: 'center', gap: '4px',
        padding: '4px 0',
    }),
    reqBox: {
        background: '#f0f9ff', border: '1px solid #bae6fd',
        borderRadius: '10px', padding: '12px 14px',
        fontSize: '0.78rem', color: '#0369a1', lineHeight: 1.6,
    },
    reqRow: { display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '4px' },

    // ── notification toggles ──
    notifRow: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 0', borderBottom: '1px solid #f1f5f9',
    },
    notifLabel: { fontSize: '0.9rem', fontWeight: 600, color: '#374151' },
    notifHelper: { fontSize: '0.78rem', color: '#9ca3af', marginTop: '2px' },

    // ── deactivate ──
    dangerCard: {
        background: '#fff5f5', border: '1.5px solid #fecaca',
        borderRadius: '12px', padding: '20px',
        marginBottom: '16px',
    },

    // ── footer bar ──
    footerBar: {
        position: 'sticky', bottom: 0,
        background: '#fff',
        borderTop: '1px solid #e2e8f0',
        padding: '14px 28px',
        display: 'flex', justifyContent: 'flex-end', gap: '10px',
        zIndex: 10,
    },
    btnPrimary: {
        padding: '10px 28px', borderRadius: '10px', border: 'none',
        background: 'linear-gradient(135deg, #005370 0%, #0291a1 100%)',
        color: '#fff', fontWeight: 700, fontSize: '0.9rem',
        cursor: 'pointer', boxShadow: '0 4px 12px rgba(2,145,161,0.28)',
        display: 'flex', alignItems: 'center', gap: '8px',
        transition: 'opacity 0.2s',
    },
    btnSecondary: {
        padding: '10px 20px', borderRadius: '10px',
        border: '1.5px solid #e2e8f0', background: '#fff',
        color: '#6b7280', fontWeight: 600, fontSize: '0.9rem',
        cursor: 'pointer',
    },
};

/* ─── Tab config ─────────────────────────────────────────────────────────── */
const TABS = [
    { id: 'account',       label: 'Account',       Icon: Store },
    { id: 'media',         label: 'Logo & Banner',  Icon: PhotoCamera },
    { id: 'hours',         label: 'Business Hours', Icon: AccessTime },
    { id: 'offers',        label: 'Offers',         Icon: LocalOffer },
    { id: 'advanced',      label: 'Advanced',       Icon: Settings },
    { id: 'notifications', label: 'Notifications',  Icon: NotificationsNone },
    /* Beside Notifications, not under Advanced: URA receipting is a decision a
       vendor is asked to make, not a setting they tune once and forget. */
    { id: 'tax',           label: 'Tax & Receipts', Icon: ReceiptOutlined },
    { id: 'pos',           label: 'POS Terminal',   Icon: PhoneAndroid },
    { id: 'deactivate',    label: 'Deactivate',     Icon: Warning },
];

// Email, SMS and WhatsApp are owned by the shared Alert settings panel
// (VendorAlertSettings). The server keeps the legacy fields; only the two
// channels that panel does not cover are still toggled here. Neither is
// dispatched for shops yet, so the helper text says so.
const NOTIF_OPTIONS = [
    { key: 'phoneCall', label: 'Phone Call',  helper: 'Get called for new or urgent orders (not yet active for shops)' },
    { key: 'dropOff',   label: 'Drop-Off',    helper: 'Notifications for drop-off type orders (not yet active for shops)' },
];

/* ═══════════════════════════════════════════════════════════════════════════ */
const RetailerWholesalerProfile = () => {
    const user = getUserFromLocalStorage();
    const classes = useStyles();
    const { fitVendorMedia, mediaCropDialog } = useVendorMediaCrop();
    const dispatch = useDispatch();
    const { profile, loading } = useSelector((state) => state.retailerWholesalerProfile);
    const vendorId = user?.result?._id;

    // Store-wide offer: the shop's sections feed the "selected sections" scope.
    const [offerSections, setOfferSections] = useState([]);
    const [savingOffer, setSavingOffer] = useState(false);
    useEffect(() => {
        let alive = true;
        if (!vendorId) return undefined;
        apiFetchRetailerWholesalerSections(vendorId)
            .then((res) => { if (alive) setOfferSections(Array.isArray(res?.data) ? res.data : []); })
            .catch(() => { if (alive) setOfferSections([]); });
        return () => { alive = false; };
    }, [vendorId]);
    const handleStoreOfferSave = async (storeOffer) => {
        setSavingOffer(true);
        try {
            await dispatch(updateRetailerWholesalerProfile(vendorId, { storeOffer }));
            toast.success(storeOffer.enabled ? 'Store-wide offer saved.' : 'Store-wide offer ended.');
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to save the offer.');
        } finally {
            setSavingOffer(false);
        }
    };

    const logoInputRef   = useRef(null);
    const bannerInputRef = useRef(null);

    const [tab, setTab] = useState('account');
    const [saving, setSaving] = useState(false);
    // Vendor team permissions: saving the vendor profile is settings:edit.
    const settingsGate = useGate('settings:edit');

    const [form, setForm] = useState({
        name: '', address: '', phone: '', email: '',
        isActive: false, latitude: '', longitude: '',
        deliveryZoneRadius: 10, imageUrl: '', bannerUrl: '', shopBannerUrl: '', tagline: '',
    });
    const [notifications, setNotifications] = useState({
        sms: false, email: false, whatsapp: false, phoneCall: false, dropOff: false,
    });

    // Baseline snapshots for dirty-state detection
    const [savedForm, setSavedForm] = useState(null);
    const [savedNotifications, setSavedNotifications] = useState(null);

    // logo state
    const [logoFile,    setLogoFile]    = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoHover,   setLogoHover]   = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);

    // banner state
    const [bannerFile,    setBannerFile]    = useState(null);
    // The saved image whose Delete button was clicked; opens the delete dialog.
    const [mediaDeleteRole, setMediaDeleteRole] = useState(null);
    const [bannerPreview, setBannerPreview] = useState(null);
    const [bannerUploading, setBannerUploading] = useState(false);

    const [deactivationConfirm, setDeactivationConfirm] = useState('');
    const [isDeactivating,      setIsDeactivating]      = useState(false);

    // POS Terminal — cashier PIN management
    const [pinValues,  setPinValues]  = useState({});  // { [cashierId]: string }
    const [pinSaving,  setPinSaving]  = useState({});  // { [cashierId]: boolean }

    useEffect(() => { dispatch(fetchRetailerWholesalerProfile(vendorId)); }, [dispatch, vendorId]);

    useEffect(() => {
        if (profile) {
            const nextForm = {
                name: '', address: '', phone: '', email: '',
                isActive: false, latitude: '', longitude: '',
                deliveryZoneRadius: 10, imageUrl: '', bannerUrl: '', shopBannerUrl: '', tagline: '',
                ...profile,
            };
            const nextNotifs = {
                sms: false, email: false, whatsapp: false, phoneCall: false, dropOff: false,
                ...(profile.notifications || {}),
            };
            setForm(nextForm);
            setNotifications(nextNotifs);
            // Commit fresh baselines so the footer buttons start disabled
            setSavedForm(nextForm);
            setSavedNotifications(nextNotifs);
        }
    }, [profile]);

    // True when any field differs from the last-saved snapshot, or a new image is staged
    const isDirty = (() => {
        if (!savedForm) return false;
        if (logoFile || bannerFile) return true;
        if (JSON.stringify(form) !== JSON.stringify(savedForm)) return true;
        if (JSON.stringify(notifications) !== JSON.stringify(savedNotifications)) return true;
        return false;
    })();

    /* ── handlers ─────────────────────────────────────────────────────────── */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleSwitch = (e) => {
        const { name, checked } = e.target;
        setForm((p) => ({ ...p, [name]: checked }));
    };

    const handleNotifChange = (e) => {
        const { name, checked } = e.target;
        setNotifications((p) => ({ ...p, [name]: checked }));
    };

    /* ── logo pick ──────────────────────────────────────────────────────── */
    const handleLogoChange = async (e) => {
        let file = e.target.files[0];
        if (!file) return;
        setLogoUploading(true);
        try {
            // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
            file = await fitVendorMedia(file, 'retailer_wholesaler', 'logo');
            if (!file) return; // crop cancelled
            const preview = await new Promise((res) => {
                const r = new FileReader();
                r.onload = () => res(r.result);
                r.readAsDataURL(file);
            });
            setLogoFile(file);
            setLogoPreview(preview);
            toast.success(`Logo ready (${LOGO_W}×${LOGO_H}px)`);
        } catch (error) {
            toast.error(error.message || 'Failed to validate logo image.');
        } finally {
            setLogoUploading(false);
            e.target.value = '';
        }
    };

    /* ── banner pick ─────────────────────────────────────────────────────── */
    const handleBannerChange = async (e) => {
        let file = e.target.files[0];
        if (!file) return;
        setBannerUploading(true);
        try {
            // An image that is not the exact size opens the crop dialog (components/VendorMediaCrop.js).
            file = await fitVendorMedia(file, 'retailer_wholesaler', 'shopBanner');
            if (!file) return; // crop cancelled
            const preview = await new Promise((res) => {
                const r = new FileReader();
                r.onload = () => res(r.result);
                r.readAsDataURL(file);
            });
            setBannerFile(file);
            setBannerPreview(preview);
            toast.success(`Banner ready (${BANNER_W}×${BANNER_H}px)`);
        } catch (error) {
            toast.error(error.message || 'Failed to validate banner image.');
        } finally {
            setBannerUploading(false);
            e.target.value = '';
        }
    };

    /* ── submit ──────────────────────────────────────────────────────────── */
    const handleOpenHoursSave = async (openHours, timezone) => {
        try {
            await apiUpdateVendorOpenHours('retailer', vendorId, {
                openHours,
                ...(timezone ? { timezone } : {}),
            });
            toast.success('Business hours updated');
            dispatch(fetchRetailerWholesalerProfile(vendorId));
        } catch (err) {
            toast.error(
                err?.response?.data?.message || 'Could not update business hours'
            );
        }
    };

    const handleSubmit = async () => {
        setSaving(true);
        let updatedForm = { ...form };

        try {
            if (logoFile) {
                const result = await uploadExactVendorProfileMedia(logoFile, {
                    vendorType: "retailer_wholesaler",
                    vendorId,
                    mediaRole: "logo",
                    entityType: "retailerWholesalerProfile",
                    entityId: vendorId,
                    source: "dashboard",
                });
                updatedForm.imageUrl = result.secure_url;
                setLogoFile(null);
                setLogoPreview(null);
            }
            if (bannerFile) {
                const result = await uploadExactVendorProfileMedia(bannerFile, {
                    vendorType: "retailer_wholesaler",
                    vendorId,
                    mediaRole: "shopBanner",
                    entityType: "retailerWholesalerProfile",
                    entityId: vendorId,
                    source: "dashboard",
                });
                updatedForm.shopBannerUrl = result.secure_url;
                updatedForm.bannerUrl = '';
                setBannerFile(null);
                setBannerPreview(null);
            }
            await dispatch(updateRetailerWholesalerProfile(vendorId, { ...updatedForm, notifications }));
            setForm(updatedForm);
            // Advance the saved baseline so the footer re-disables immediately
            setSavedForm(updatedForm);
            setSavedNotifications({ ...notifications });
            toast.success('Profile updated successfully.');
        } catch (err) {
            // The server's own reason (for example which coordinate is wrong),
            // not the transport's "Request failed with status code 400".
            notify.fromError(err, 'Could not save your profile');
        } finally {
            setSaving(false);
        }
    };

    // An unsaved pick is only a local preview: discard it with no server call
    // and no confirmation. A saved image opens VendorProfileMediaDeleteDialog,
    // which shows every saved image and lets the vendor tick more than one.
    const handleRemoveMedia = (mediaRole) => {
        const isLogo = mediaRole === 'logo';
        const stagedFile = isLogo ? logoFile : bannerFile;
        if (stagedFile) {
            if (isLogo) { setLogoFile(null); setLogoPreview(null); }
            else { setBannerFile(null); setBannerPreview(null); }
            return;
        }
        setMediaDeleteRole(mediaRole);
    };

    /* ── deactivate / reactivate ─────────────────────────────────────────── */
    const handleDeactivate = async () => {
        if (deactivationConfirm !== profile.name) {
            toast.error('Shop name does not match. Please type it exactly.');
            return;
        }
        setIsDeactivating(true);
        try {
            await dispatch(updateRetailerWholesalerProfile(vendorId, { isActive: false }));
            dispatch(fetchRetailerWholesalerProfile(vendorId));
            toast.success('Shop deactivated.');
        } catch { toast.error('Failed to deactivate shop.'); }
        finally { setIsDeactivating(false); setDeactivationConfirm(''); }
    };

    const confirmDialog = useConfirmDialog();

    const handleReactivate = () => {
        confirmDialog.ask({
            title: 'Reactivate your shop?',
            body: 'It becomes visible to customers again straight away.',
            tone: 'success',
            confirmLabel: 'Reactivate',
            busyLabel: 'Reactivating...',
            onConfirm: async () => {
                setIsDeactivating(true);
                try {
                    await dispatch(updateRetailerWholesalerProfile(vendorId, { isActive: true }));
                    dispatch(fetchRetailerWholesalerProfile(vendorId));
                    toast.success('Shop reactivated.');
                } catch { toast.error('Failed to reactivate shop.'); }
                finally { setIsDeactivating(false); }
            },
        });
    };

    /* ── loading ─────────────────────────────────────────────────────────── */
    if (loading && !profile) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress style={{ color: '#0291a1' }} />
            </div>
        );
    }

    const currentLogo   = logoPreview   || form.imageUrl  || null;
    const currentBanner = bannerPreview || form.shopBannerUrl || form.bannerUrl || null;

    /* ═══════════════════════════════════════════════════════════════════════ */
    return (
        <div style={S.page}>

            {/* ── Reactivation alert ── */}
            {profile && !profile.isActive && (
                <MuiAlert
                    severity="warning"
                    style={{ margin: '0 28px 16px', borderRadius: '10px' }}
                    action={
                        <button
                            style={{ ...S.btnPrimary, padding: '6px 16px', fontSize: '0.8rem' }}
                            onClick={handleReactivate}
                            disabled={isDeactivating}
                        >
                            Reactivate Now
                        </button>
                    }
                >
                    <AlertTitle>Shop Deactivated</AlertTitle>
                    Your shop is hidden from customers and not accepting orders.
                </MuiAlert>
            )}

            {/* ── Banner ── */}
            <div style={S.bannerWrap}>
                {currentBanner
                    ? <img src={currentBanner} alt="Shop banner" style={S.bannerImg} />
                    : null
                }
                <div style={S.bannerOverlay}>
                    <button
                        style={S.bannerEditBtn}
                        onClick={() => bannerInputRef.current?.click()}
                    >
                        <Panorama style={{ fontSize: '1rem' }} />
                        {currentBanner ? 'Change Banner' : 'Add Banner'}
                    </button>
                    <input
                        ref={bannerInputRef}
                        type="file"
                        hidden
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={handleBannerChange}
                    />
                </div>
                {bannerUploading && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
                        <Spinner animation="border" style={{ color: '#fff' }} />
                    </div>
                )}
            </div>

            {/* ── Profile header — sits cleanly below banner ── */}
            <div style={S.profileHeader}>
                {/* Logo avatar */}
                <div
                    style={S.logoWrap}
                    onMouseEnter={() => setLogoHover(true)}
                    onMouseLeave={() => setLogoHover(false)}
                    onClick={() => logoInputRef.current?.click()}
                    title="Click to change logo"
                >
                    {logoUploading ? (
                        <div style={S.logoPlaceholder}>
                            <Spinner animation="border" size="sm" />
                        </div>
                    ) : currentLogo ? (
                        <img src={currentLogo} alt="Shop logo" style={S.logoImg} />
                    ) : (
                        <div style={S.logoPlaceholder}>
                            <Store style={{ fontSize: '2rem' }} />
                        </div>
                    )}
                    <div style={{ ...S.logoEditOverlay, opacity: logoHover ? 1 : 0 }}>
                        <AddAPhoto style={{ color: '#fff', fontSize: '1.2rem' }} />
                    </div>
                    <input
                        ref={logoInputRef}
                        type="file"
                        hidden
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={handleLogoChange}
                    />
                </div>

                {/* Shop name + status */}
                <div>
                    <div style={S.shopName}>{form.name || 'Your Shop'}</div>
                    <div style={S.shopStatus(form.isActive)}>
                        <CheckCircleOutline style={{ fontSize: '0.82rem' }} />
                        {form.isActive ? 'Active' : 'Inactive'}
                    </div>
                </div>
            </div>

            {/* ── Tab bar ── */}
            <div style={S.tabBar}>
                {TABS.map(({ id, label, Icon }) => (
                    <button
                        key={id}
                        style={S.tab(tab === id)}
                        onClick={() => setTab(id)}
                    >
                        <Icon style={{ fontSize: '1rem' }} />
                        {label}
                    </button>
                ))}
            </div>

            {/* ═══════════════════════════════════════════════════════════════
                TAB: Account
            ═══════════════════════════════════════════════════════════════ */}
            {tab === 'account' && (
                <div style={S.card}>
                    <div style={S.sectionTitle}>Shop Details</div>
                    <div style={S.fieldGrid}>
                        <Form.Group style={S.fieldFull}>
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151' }}>
                                Shop Name
                            </Form.Label>
                            <Form.Control
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                readOnly
                                style={{ borderRadius: '8px', background: '#f8fafc', fontWeight: 600 }}
                            />
                            <Form.Text style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                Contact support to change your registered shop name.
                            </Form.Text>
                        </Form.Group>

                        <VendorTaglineControl
                            value={form.tagline}
                            onChange={handleChange}
                            groupStyle={S.fieldFull}
                            labelStyle={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151' }}
                            controlStyle={{ borderRadius: '8px' }}
                        />

                        <Form.Group style={S.fieldFull}>
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151' }}>Address</Form.Label>
                            <Form.Control
                                name="address"
                                value={form.address || ''}
                                onChange={handleChange}
                                placeholder="e.g. Plot 12, Kampala Road, Kampala"
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151' }}>Phone</Form.Label>
                            <Form.Control
                                name="phone"
                                value={form.phone || ''}
                                onChange={handleChange}
                                placeholder="+256..."
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151' }}>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={form.email || ''}
                                onChange={handleChange}
                                placeholder="shop@email.com"
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>

                        <div style={S.sectionTitle}>Location Coordinates</div>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', alignSelf: 'center' }}>
                            Locked to prevent accidental changes — contact support to update.
                        </div>

                        <Form.Group>
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151' }}>Latitude</Form.Label>
                            <Form.Control
                                type="number"
                                name="latitude"
                                value={form.latitude || ''}
                                onChange={handleChange}
                                step="any"
                                placeholder="e.g. 0.3476"
                                style={{ borderRadius: '8px' }}
                                disabled
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151' }}>Longitude</Form.Label>
                            <Form.Control
                                type="number"
                                name="longitude"
                                value={form.longitude || ''}
                                onChange={handleChange}
                                step="any"
                                placeholder="e.g. 32.5825"
                                style={{ borderRadius: '8px' }}
                                disabled
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem', color: '#374151' }}>Delivery Zone Radius (km)</Form.Label>
                            <Form.Control
                                type="number"
                                name="deliveryZoneRadius"
                                value={form.deliveryZoneRadius || 10}
                                onChange={handleChange}
                                min={1}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                TAB: Logo & Banner
            ═══════════════════════════════════════════════════════════════ */}
            {tab === 'media' && (
                <div style={S.card}>

                    {/* Logo */}
                    <div style={S.sectionTitle}>Shop Logo</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
                        <div>
                            <div
                                style={{ ...S.imgPanel, height: '220px', cursor: 'pointer' }}
                                onClick={() => logoInputRef.current?.click()}
                            >
                                {logoUploading ? (
                                    <Spinner animation="border" />
                                ) : currentLogo ? (
                                    <img
                                        src={currentLogo}
                                        alt="Logo preview"
                                        style={{ maxHeight: '190px', maxWidth: '100%', objectFit: 'contain', borderRadius: '8px' }}
                                    />
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: '#9ca3af' }}>
                                        <AddAPhoto style={{ fontSize: '2.5rem' }} />
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Click to upload logo</span>
                                    </div>
                                )}
                            </div>
                            <div style={S.imgActions}>
                                <button
                                    style={S.imgActionBtn('#0291a1')}
                                    onClick={() => logoInputRef.current?.click()}
                                >
                                    <AddAPhoto style={{ fontSize: '0.9rem' }} />
                                    {currentLogo ? 'Replace' : 'Upload'}
                                </button>
                                {currentLogo && (
                                    <button
                                        style={S.imgActionBtn('#dc2626')}
                                        onClick={() => handleRemoveMedia('logo')}
                                    >
                                        <DeleteOutline style={{ fontSize: '0.9rem' }} /> Remove
                                    </button>
                                )}
                            </div>
                            <input ref={logoInputRef} type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleLogoChange} />
                        </div>
                        <div style={S.reqBox}>
                            <div style={{ fontWeight: 700, marginBottom: '8px', color: '#0369a1' }}>Logo requirements</div>
                            <div style={S.reqRow}>
                                <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                <span>JPG, PNG, or WebP, max 2 MB</span>
                            </div>
                            <div style={S.reqRow}>
                                <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                <span>Exact size: <strong>{LOGO_W} × {LOGO_H} px</strong>. Any other size opens a crop step.</span>
                            </div>
                            <div style={S.reqRow}>
                                <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                <span>Displayed as a square tile in the mobile app and web storefront</span>
                            </div>
                            <div style={S.reqRow}>
                                <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                <span>Use a white or plain background for best results</span>
                            </div>
                        </div>
                    </div>

                    {/* Banner */}
                    <div style={S.sectionTitle}>Shop Banner</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div>
                            <div
                                style={{ ...S.imgPanel, height: '110px', cursor: 'pointer' }}
                                onClick={() => bannerInputRef.current?.click()}
                            >
                                {bannerUploading ? (
                                    <Spinner animation="border" />
                                ) : currentBanner ? (
                                    <img
                                        src={currentBanner}
                                        alt="Banner preview"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: '#9ca3af' }}>
                                        <Panorama style={{ fontSize: '2.5rem' }} />
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Click to upload banner</span>
                                    </div>
                                )}
                            </div>
                            <div style={S.imgActions}>
                                <button
                                    style={S.imgActionBtn('#0291a1')}
                                    onClick={() => bannerInputRef.current?.click()}
                                >
                                    <AddAPhoto style={{ fontSize: '0.9rem' }} />
                                    {currentBanner ? 'Replace' : 'Upload'}
                                </button>
                                {currentBanner && (
                                    <button
                                        style={S.imgActionBtn('#dc2626')}
                                        onClick={() => handleRemoveMedia('shopBanner')}
                                    >
                                        <DeleteOutline style={{ fontSize: '0.9rem' }} /> Remove
                                    </button>
                                )}
                            </div>
                            <input ref={bannerInputRef} type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={handleBannerChange} />
                        </div>
                        <div style={S.reqBox}>
                            <div style={{ fontWeight: 700, marginBottom: '8px', color: '#0369a1' }}>Banner requirements</div>
                            <div style={S.reqRow}>
                                <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                <span>JPG, PNG, or WebP, max 5 MB</span>
                            </div>
                            <div style={S.reqRow}>
                                <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                <span>Exact size: <strong>{BANNER_W} × {BANNER_H} px</strong>. Any other size opens a crop step.</span>
                            </div>
                            <div style={S.reqRow}>
                                <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                <span>Displayed as a full-width hero strip on your storefront page</span>
                            </div>
                            <div style={S.reqRow}>
                                <InfoOutlined style={{ fontSize: '0.9rem', marginTop: '1px', flexShrink: 0 }} />
                                <span>Avoid placing important text near the edges — it may be cropped on smaller screens</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                TAB: Advanced
            ═══════════════════════════════════════════════════════════════ */}
            {tab === 'advanced' && (
                <div style={S.card}>
                    <div style={S.sectionTitle}>Shop Visibility</div>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={!!form.isActive}
                                onChange={handleSwitch}
                                name="isActive"
                                color="primary"
                            />
                        }
                        label={form.isActive ? 'Shop is Active' : 'Shop is Deactivated'}
                        classes={{ label: classes.muiLabel }}
                    />
                    <p style={{ fontSize: '0.82rem', color: '#9ca3af', marginTop: '8px' }}>
                        When deactivated, your shop is hidden from all customers and stops accepting new orders.
                        Existing in-progress orders still need to be fulfilled.
                    </p>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                TAB: Notifications
            ═══════════════════════════════════════════════════════════════ */}
            {/* ── Business Hours ──
                Reuses the same OpenHoursManager the restaurant profile uses —
                the component has no vendor-type coupling. Hours save on their
                own dispatch rather than through the sticky footer, matching
                RestaurantProfile: an operating-hours change should take effect
                immediately, not sit behind an unrelated dirty-form save. */}
            {tab === 'hours' && (
                <div style={S.card}>
                    <OpenHoursManager
                        vendorLabel="shop"
                        // Shops trade 24/7 until they save hours; the editor
                        // reads this instead of guessing from the label.
                        tradingPolicy="open_until_set"
                        vendorType="retailer"
                        vendorId={vendorId}
                        openHours={profile?.openHours}
                        timezone={profile?.timezone || 'Africa/Kampala'}
                        onSave={handleOpenHoursSave}
                        onValidationError={(msg) => toast.error(msg)}
                        isLoading={loading}
                    />
                </div>
            )}

            {/* ── Store-wide offer (vendor discounts) ── */}
            {tab === 'offers' && (
                <div style={S.card}>
                    <StoreOfferCard
                        value={profile?.storeOffer}
                        sections={offerSections}
                        saving={savingOffer}
                        onSave={handleStoreOfferSave}
                    />
                </div>
            )}

            {tab === 'tax' && (
                <div style={S.card}>
                    <div style={S.sectionTitle}>Tax and receipts</div>
                    {/* Shared VendorTaxSettings panel, backed by /api/vendor-tax.
                        Self-scoped: it resolves this account's own shop server-side,
                        so it takes no props and cannot be pointed at anyone else. */}
                    <VendorTaxSettings />
                </div>
            )}

            {tab === 'notifications' && (
                <div style={S.card}>
                    <VendorAlertSettings />
                    <div style={{ borderTop: '1px solid #eef1f6', margin: '18px 0' }} />
                    <div style={S.sectionTitle}>Voice and drop-off alerts</div>
                    {NOTIF_OPTIONS.map(({ key, label, helper }) => (
                        <div key={key} style={S.notifRow}>
                            <div>
                                <div style={S.notifLabel}>{label}</div>
                                <div style={S.notifHelper}>{helper}</div>
                            </div>
                            <Switch
                                checked={!!notifications[key]}
                                onChange={handleNotifChange}
                                name={key}
                                color="primary"
                                size="small"
                            />
                        </div>
                    ))}
                    <div style={{ borderTop: '1px solid #eef1f6', margin: '18px 0 0', paddingTop: 18 }}>
                        <AlertSoundPrefs />
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                TAB: POS Terminal
            ═══════════════════════════════════════════════════════════════ */}
            {tab === 'pos' && (() => {
                const user = getUserFromLocalStorage();
                const ownerCashier = {
                    cashierId: user?.result?._id,
                    displayName: user?.result?.name || 'You (Owner)',
                    username: user?.result?.email || '',
                    role: 'MANAGER',
                };
                const helperCashiers = (profile?.managers ?? []).map((m) =>
                    typeof m === 'object'
                        ? { cashierId: m._id, displayName: m.name || m.email, username: m.email, role: 'CASHIER' }
                        : { cashierId: m, displayName: m, username: '', role: 'CASHIER' }
                );
                const allCashiers = [ownerCashier, ...helperCashiers];

                const handleSetPin = async (cashierId) => {
                    const newPin = (pinValues[cashierId] || '').trim();
                    if (!/^\d{4,6}$/.test(newPin)) {
                        toast.error('PIN must be 4–6 digits.');
                        return;
                    }
                    setPinSaving((p) => ({ ...p, [cashierId]: true }));
                    try {
                        await apiSetCashierPin({ cashierId, newPin });
                        toast.success('PIN updated. Re-provision the terminal to apply.');
                        setPinValues((p) => ({ ...p, [cashierId]: '' }));
                    } catch (err) {
                        toast.error(err?.response?.data?.message || 'Failed to set PIN.');
                    } finally {
                        setPinSaving((p) => ({ ...p, [cashierId]: false }));
                    }
                };

                return (
                    <div style={S.card}>
                        <div style={S.sectionTitle}>Cashier PINs</div>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '20px' }}>
                            Set a 4–6 digit PIN for each cashier. PINs are stored as a secure hash and
                            will be loaded onto the terminal next time it provisions from the Dashboard.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {allCashiers.map((c) => (
                                <div key={c.cashierId} style={{
                                    display: 'flex', alignItems: 'center', gap: '16px',
                                    padding: '14px 18px', borderRadius: '12px',
                                    background: '#f8fafc', border: '1px solid #e2e8f0',
                                }}>
                                    {/* Avatar */}
                                    <div style={{
                                        width: '40px', height: '40px', borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #6C3FC5 0%, #9B72D8 100%)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#fff', fontWeight: 700, fontSize: '1rem', flexShrink: 0,
                                    }}>
                                        {(c.displayName || '?')[0].toUpperCase()}
                                    </div>

                                    {/* Info */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {c.displayName}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                            {c.role}
                                            {c.username ? ` · ${c.username}` : ''}
                                        </div>
                                    </div>

                                    {/* PIN input */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                        <div style={{ position: 'relative' }}>
                                            <LockOutlined style={{
                                                position: 'absolute', left: '10px', top: '50%',
                                                transform: 'translateY(-50%)',
                                                fontSize: '1rem', color: '#9ca3af',
                                            }} />
                                            <input
                                                type="password"
                                                inputMode="numeric"
                                                maxLength={6}
                                                placeholder="New PIN"
                                                value={pinValues[c.cashierId] || ''}
                                                onChange={(e) => setPinValues((p) => ({ ...p, [c.cashierId]: e.target.value.replace(/\D/g, '') }))}
                                                style={{
                                                    width: '110px', padding: '8px 10px 8px 32px',
                                                    borderRadius: '8px', border: '1.5px solid #d1d5db',
                                                    fontSize: '0.9rem', letterSpacing: '4px',
                                                    outline: 'none',
                                                }}
                                            />
                                        </div>
                                        <button
                                            style={{
                                                ...S.btnPrimary,
                                                padding: '8px 16px', fontSize: '0.8rem',
                                                opacity: pinSaving[c.cashierId] ? 0.6 : 1,
                                            }}
                                            disabled={pinSaving[c.cashierId]}
                                            onClick={() => handleSetPin(c.cashierId)}
                                        >
                                            {pinSaving[c.cashierId] ? 'Saving…' : 'Set PIN'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{
                            marginTop: '24px', padding: '14px 18px', borderRadius: '10px',
                            background: '#eff6ff', border: '1px solid #bfdbfe',
                            fontSize: '0.82rem', color: '#1e40af',
                        }}>
                            <strong>How it works:</strong> PINs are hashed with SHA-256 and never stored in plain text.
                            After setting PINs here, the vendor must re-provision the terminal
                            (tap <em>Connect to Dashboard</em> in the Android app) to pull the updated hashes.
                            The default placeholder PIN is <strong>0000</strong> until overridden here.
                        </div>
                    </div>
                );
            })()}

            {/* ═══════════════════════════════════════════════════════════════
                TAB: Deactivate
            ═══════════════════════════════════════════════════════════════ */}
            {tab === 'deactivate' && (
                <div style={S.card}>
                    {profile && profile.isActive ? (
                        <>
                            <div style={S.dangerCard}>
                                <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: '8px', fontSize: '0.95rem' }}>
                                    Deactivating will hide your shop
                                </div>
                                <ul style={{ fontSize: '0.85rem', color: '#374151', paddingLeft: '18px', margin: 0 }}>
                                    <li>Your shop will be hidden from all customers</li>
                                    <li>You will not receive any new orders</li>
                                    <li>Existing orders must still be fulfilled</li>
                                    <li>You can reactivate at any time</li>
                                </ul>
                            </div>
                            <p style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '10px' }}>
                                To confirm, type your shop name exactly: <strong>{profile.name}</strong>
                            </p>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder={`Type "${profile.name}" to confirm`}
                                value={deactivationConfirm}
                                onChange={(e) => setDeactivationConfirm(e.target.value)}
                                style={{ marginBottom: '14px' }}
                            />
                            <button
                                style={{
                                    ...S.btnPrimary,
                                    background: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)',
                                    boxShadow: '0 4px 12px rgba(185,28,28,0.3)',
                                    opacity: (deactivationConfirm !== profile?.name || isDeactivating) ? 0.5 : 1,
                                }}
                                disabled={deactivationConfirm !== profile?.name || isDeactivating}
                                onClick={handleDeactivate}
                            >
                                {isDeactivating ? <Spinner animation="border" size="sm" /> : 'Deactivate Shop'}
                            </button>
                        </>
                    ) : (
                        <MuiAlert severity="info" style={{ borderRadius: '10px' }}>
                            <AlertTitle>Shop Already Deactivated</AlertTitle>
                            Use the "Reactivate Now" button at the top of the page to make your shop visible to customers again.
                        </MuiAlert>
                    )}
                </div>
            )}

            {/* ── Sticky footer ── */}
            <div style={S.footerBar}>
                <button
                    style={{ ...S.btnSecondary, opacity: isDirty ? 1 : 0.4, cursor: isDirty ? 'pointer' : 'default' }}
                    onClick={() => isDirty && dispatch(fetchRetailerWholesalerProfile(vendorId))}
                    disabled={!isDirty}
                >
                    Discard Changes
                </button>
                <button
                    style={{ ...S.btnPrimary, opacity: (saving || !isDirty) ? 0.4 : 1, cursor: (saving || !isDirty) ? 'default' : 'pointer' }}
                    onClick={handleSubmit}
                    disabled={(saving || !isDirty) || !settingsGate.allowed}
                    title={settingsGate.title}
                >
                    {saving
                        ? <><Spinner animation="border" size="sm" /> Saving…</>
                        : 'Save Changes'
                    }
                </button>
            </div>

            <ConfirmDialog {...confirmDialog.props} />
            {mediaCropDialog}
            <VendorProfileMediaDeleteDialog
                role={mediaDeleteRole}
                vendorType="retailer_wholesaler"
                vendorId={vendorId}
                vendorName={profile?.name}
                media={form}
                owner="your shop"
                onClose={() => setMediaDeleteRole(null)}
                onRemoved={(cleared) => {
                    // The server has already cleared these, so the baseline
                    // moves too: deleting an image must not mark the form dirty.
                    setForm((p) => ({ ...p, ...cleared }));
                    setSavedForm((p) => (p ? { ...p, ...cleared } : p));
                }}
            />
        </div>
    );
};

export default RetailerWholesalerProfile;
